/**
 * Last workspace the user switched to inside each agent, kept until logout (ASC-10715).
 *
 * localStorage, not sessionStorage: a new tab must reopen the agent in the same
 * workspace. Keyed by user + org so a multi-org user or a second account on the same
 * browser never inherits another identity's choice. Read BEFORE the agent's preferred
 * workspace (ASC-6010) and never writes that preference — a switch is session-scoped,
 * the preference is a deliberate separate action.
 *
 * Cleared by `clearLastWorkspaces()` from two hooks: AuthzenProvider's `onLogout` (the
 * logout action) and /login page mount, which every session end reaches — idle/max
 * timeout, revoked session and dead tokens on page load included, the last via a server
 * redirect that runs no client code. A session killed in Keycloak is only noticed at the
 * next refresh tick or page load, so it clears then.
 *
 * Every storage access is try/catch'd: private mode, blocked site data and SSR throw or
 * lack `window`, and a missing value must degrade to the preferred workspace, never error.
 */

const KEY_PREFIX = 'scorpio:last-ws:'

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage
  } catch {
    return null
  }
}

function storageKey(userId: string, orgId: string, agentId: string): string | null {
  // An unresolved session (empty ids) must not read or write a shared ':'-keyed slot.
  if (!userId || !orgId || !agentId) return null
  return `${KEY_PREFIX}${userId}:${orgId}:${agentId}`
}

export function readLastWorkspace(userId: string, orgId: string, agentId: string): string | null {
  const key = storageKey(userId, orgId, agentId)
  if (!key) return null
  try {
    return storage()?.getItem(key) || null
  } catch {
    return null
  }
}

export function writeLastWorkspace(
  userId: string,
  orgId: string,
  agentId: string,
  workspaceId: string,
): void {
  const key = storageKey(userId, orgId, agentId)
  if (!key || !workspaceId) return
  try {
    storage()?.setItem(key, workspaceId)
  } catch {
    // Quota / blocked storage — the switch still applies for this page's lifetime.
  }
}

export function forgetLastWorkspace(userId: string, orgId: string, agentId: string): void {
  const key = storageKey(userId, orgId, agentId)
  if (!key) return
  try {
    storage()?.removeItem(key)
  } catch {
    // Best-effort.
  }
}

/** Drops every user's entries, not just the current one — logout ends the browser session. */
export function clearLastWorkspaces(): void {
  const store = storage()
  if (!store) return
  try {
    const keys: string[] = []
    for (let i = 0; i < store.length; i++) {
      const key = store.key(i)
      if (key?.startsWith(KEY_PREFIX)) keys.push(key)
    }
    // Collected first: removing while iterating by index shifts the remaining keys.
    for (const key of keys) store.removeItem(key)
  } catch {
    // Best-effort.
  }
}
